import EventEmitter from 'events';

import axios, { Cancel } from 'axios';
import qs from 'querystring';
import commonFunc from '@/common/commonFunction';
import apiConfig from '@/api/apiConfig';
import router from '../router';
import store from '@/store/store';
import { MSEnum } from '@/common/enumeration';
import { removeStorage } from '../store/vuex-persistance-plugin';
import { isNullOrEmpty } from '../common/objectUtil';
import msgBox from '@/common/messageBox';
import i18n from '@/i18n/i18n';
import {
	ModuleCompany,
	ModuleAuth,
	ModuleDictionary,
	ModuleTaxPayerInfo,
	ModuleLicenseInfo,
	ModuleOptions,
	ModuleServiceTVAN,
	ModuleUser,
	ResetModules,
} from '../store/module-const';
import { destroyMisaNotification } from '@/integrations/misaNotification.js';
import { destroyAmisHeader } from '@/integrations/amisHeader.js';
import { destroyLicenseNotification } from '@/integrations/licenseNotification.js';
// 'loggedIn' is used in other parts of application. So, Don't forget to change there also
const loginEventKey = 'loginEvent';
const userInfo = 'userInfo';

/**
 * Đọc giá trị của cookie theo tên
 * @param {string} name Tên cookie cần đọc
 * @returns {string|null} Giá trị cookie, null nếu không tồn tại
 */
function getCookie(name) {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) {
		return parts.pop().split(';').shift();
	}
	return null;
}

class AuthService extends EventEmitter {
	constructor() {
		super();
		this._sessionExchanged = false;
		//Cờ chặn show popup "Phiên làm việc đã thay đổi" nhiều lần khi nhiều request cùng bị 401
		this._isShowingSessionChanged = false;
		axios.interceptors.response.use(
			(response) => {
				return response;
			},
			(error) => {
				if (
					error &&
					error.response &&
					error.response.status ==
						MSEnum.StatusCode.Status403Forbidden
				) {
					if (
						error.response.data.Code ==
						MSEnum.ServiceStatusCode.Status06NotPermittedAccess
					) {
						//Không có quyền truy cập
						if (this.isAmisApp()) {
							// App chạy trong AMIS: session AMIS vẫn còn hạn, chỉ token MTax hết hạn
							// → thông báo phiên thay đổi, chờ user bấm "Tải lại" mới clear storage + điều hướng về /loading
							this._showSessionChangedPopup();
						} else {
							// App thường: hết phiên → về màn đăng nhập MTax
							router.push('/login/startwithtaxcode');
							this.logOut();
						}
					} else if (
						error.response.data.Code ==
						MSEnum.ServiceStatusCode.Status11InvalidAdminAccess
					) {
						//Không có quyền admin
						router.push('/pages/not-permission');
					} else if (
						error.response.data.Code ==
						MSEnum.ServiceStatusCode.Status27MaintainSystem
					) {
						//Đang bảo trì
						router.push('/pages/maintancesystem');
					}
				} else if (
					error &&
					error.response &&
					error.response.status ==
						MSEnum.StatusCode.Status401Unauthorized
				) {
					if (this.isAmisApp()) {
						// App chạy trong AMIS: session AMIS vẫn còn hạn, chỉ token MTax hết hạn
						// → thông báo phiên thay đổi, chờ user bấm "Tải lại" mới clear storage + điều hướng về /loading
						this._showSessionChangedPopup();
					} else {
						// App thường: hết phiên → về màn đăng nhập MTax
						router.push('/login');
						this.logOut();
					}
				}
				//Status422UnprocessableEntity
				else if (
					error &&
					error.response &&
					error.response.status ==
						MSEnum.StatusCode.Status422UnprocessableEntity
				) {
					//Nếu là lỗi nghiệp vụ thì hiển thị lỗi cho người dùng
					if (
						error.response.data &&
						error.response.data.IsErrorBusiness
					) {
						return error.response.data;
					} else {
						if (this.isAmisApp()) {
							this._showSessionChangedPopup();
						} else {
							router.push('/login');
							this.logOut();
						}
					}
				} else {
					if (error instanceof Cancel) {
						//Cancel request của dịch vụ nên không xử lý gì
						console.log('Cancel request!');
					} else {
						return Promise.reject(error);
					}
				}
			}
		);
	}

	/**
	 * Điều hướng sang màn đăng nhập
	 * @param {*} userInfo
	 */
	logoutExternal() {
		window.location = location.origin + '/login';
	}

	/**
	 * Cập nhật thông tin người dùng, công ty xuống State
	 * Lấy thông tin người nộp thuế
	 * Lấy thông tin license
	 * Điều hướng sang màn Chọn MST làm việc nếu chưa có MST mặc định, ngoài ra cho vào Dashboard	 *
	 * @param {*} data
	 */
	localLogin(data) {
		const me = this;
		me.localLoginIntegration(data, (isWorkingWithTaxCode) => {
			if (isWorkingWithTaxCode) {
				router.push('/');
			} else {
				router.push('/login/startwithtaxcode');
			}
		});
	}

	//Đăng nhập thiết lập dữ liệu
	localLoginIntegration(data, callback) {
		const me = this;
		// Cập nhật thông tin đăng nhập của MISAID
		store.commit(`${ModuleAuth}/updateMisaToken`, data);

		// Cập nhật thông tin đăng nhập của Mtax
		store.commit(`${ModuleAuth}/updateAccessToken`, data.AppData);

		// Cập nhật thông tin đăng nhập của amis
		//TODO: Check tạm ở đây khi nào đăng nhập qua amiss thì test lại
		if (data.AmisAuthData) {
			store.commit(`${ModuleAuth}/updateAmisAuthData`, data.AmisAuthData);
		}

		//Đánh dấu xem người dùng này đã làm việc với MST nào hay chưa
		let isWorkingWithTaxCode = false;

		// Kiểm tra thông tin người dùng xem đã làm việc với mã số thuế nào chưa?
		if (
			data.AppData.User &&
			data.AppData.User.TaxCodeWorking &&
			data.AppData.RoleUser &&
			data.AppData.RoleUser.IsActive
		) {
			// Cập nhật thông tin công ty
			store.commit(
				`${ModuleCompany}/updateCompanyInfo`,
				data.AppData.RoleUser
			);

			// Cập nhật lại license
			store.commit(
				`${ModuleLicenseInfo}/updateLicenseInfo`,
				data.AppData.LicenseInfo
			);

			//Cập nhật thông tin người nộp thuế
			store.commit(
				`${ModuleTaxPayerInfo}/setTaxPayerInfo`,
				data.AppData.TaxPayerInfo
			);

			// Cập nhật UserTax hiện tại (theo MisaId + TaxCode làm việc) — dùng cho getter AmisConvertStatus của user đăng nhập
			store.commit(
				`${ModuleUser}/setCurrentUserTax`,
				data.AppData.UserTax
			);

			//Cập nhật thông tin tùy chọn của hệ thống
			store.commit(
				`${ModuleOptions}/updateOptionsData`,
				data.AppData.OptionsData
			);

			//Cập nhật thông tin khai thuế
			store.commit(
				`${ModuleServiceTVAN}/updateDecServiceRegisInfo`,
				data.AppData.DecRegis
			);

			//Cập nhật thông tin nộp thuế
			store.commit(
				`${ModuleServiceTVAN}/updatePayServiceRegisInfo`,
				data.AppData.PayRegis
			);

			//Đã có mã số thuế làm việc
			isWorkingWithTaxCode = true;
		}

		//Gọi ra ngoài
		setTimeout(() => {
			if (callback) {
				callback(isWorkingWithTaxCode);
			}
		}, 500);
	}

	//Đăng xuất xóa phiên đăng nhập
	logOut(callback) {
		this.clearAllOldSession();

		//Gọi ra ngoài xử lý
		if (callback) callback();

		//Điều hướng sang màn đăng nhập
		this.logoutExternal();
	}

	// Đăng xuất khỏi hệ thống từ luồng amis
	logOutFromAmis(loginUrl) {
		this.clearAllOldSession();

		//Điều hướng sang trang login
		window.location = loginUrl;
	}

	/**
	 * Kiểm tra app hiện tại có đang chạy nhúng trong AMIS hay không
	 * @returns {boolean}
	 */
	isAmisApp() {
		return !!(
			window.__env &&
			window.__env.siteConfig &&
			window.__env.siteConfig.isAmisApp
		);
	}

	/**
	 * Hiện popup thông báo "Phiên làm việc đã thay đổi" khi API trả 401/422 với app chạy trong AMIS
	 * (session AMIS còn hiệu lực, chỉ token MTax không còn hợp lệ).
	 * User bấm "Tải lại" mới clear storage + điều hướng sang /loading để xác thực lại (LoadingAuth.vue
	 * sẽ tự exchangeSession lại qua session AMIS, không bắt đăng nhập lại từ đầu).
	 * Có cờ chặn để nhiều request cùng lúc bị 401 không mở nhiều popup chồng nhau.
	 */
	_showSessionChangedPopup() {
		if (this._isShowingSessionChanged) {
			return;
		}
		this._isShowingSessionChanged = true;

		msgBox
			.show({
				message: i18n.t('i18nCommon.SessionChanged'),
				icon: 'mi-icon-waring-55',
				line: true,
				close: false,
				btnConfig: [
					{
						key: 'Yes',
						target: 'Center',
						text: i18n.t('i18nCommon.Refresh'),
						focus: true,
					},
				],
			})
			.then(() => {
				this.clearAllOldSession();
				this._isShowingSessionChanged = false;
				router.push('/loading');
			});
	}

	clearAllOldSession() {
		// Reset toàn bộ state in-memory về default
		ResetModules.forEach((m) => store.commit(`${m}/resetState`));

		// Reset riêng auth: resetState đã active (xoá token, expiredDate, userInfo)
		// nhưng không nằm trong ResetModules để tránh flow đổi MST (changeTaxCode) mất session
		store.commit(`${ModuleAuth}/resetState`);

		//Xóa dữ liệu phiên cũ trước khi xác thực lại, tránh request khác dùng phải token chết
		removeStorage();

		//Hủy kết nối MISA Notification In-App trước khi logout
		destroyMisaNotification();

		//Hủy AMIS header nếu đang active
		destroyAmisHeader();

		//Hủy PLF license notification (ALN) nếu đang hiển thị — không để sót instance khi logout/session hết hạn
		destroyLicenseNotification();
	}

	/**
	 * Kiểm tra TenantID trong amisAuthData của module auth có khớp với TenantID tại cookie x-tenantid hay không
	 * @returns {boolean} true nếu cả hai bằng nhau, ngược lại false
	 */
	isTenantIdMatched() {
		const amisTenantId =
			(store.state.auth.amisAuthData &&
				store.state.auth.amisAuthData.TenantID) ||
			'';
		const cookieTenantId = getCookie('x-tenantid') || '';
		return (
			amisTenantId.toString().toLowerCase() ===
			cookieTenantId.toString().toLowerCase()
		);
	}

	/**
	 * Kiểm tra xem thời gian của token trong hệ thống còn không
	 * @returns
	 */
	isExpiredDate() {
		const timeToken = commonFunc
			.parseStringToDate(store.state.auth.expiredDate)
			.getTime();
		return new Date(Date.now()).getTime() < timeToken;
	}

	//Kiểm tra xem còn session đăng nhập ko
	isAuthenticated() {
		const me = this;

		//Lưu ý: exchangeSession + localLoginIntegration cho amis app đã chuyển sang màn /loading (LoadingAuth.vue)
		//Hàm này chỉ kiểm tra hạn token, không còn gọi exchangeSession
		return me.isExpiredDate();
	}

	/**
	 * Kiểm tra cập nhật phiên bản Admin Area
	 */
	isUpdateAdminArea() {
		if (
			store.state &&
			store.state.taxpayerinfo &&
			store.state.taxpayerinfo.taxPayerInfo
		) {
			return store.state.taxpayerinfo.taxPayerInfo.IsUpdateAdminArea;
		}
		return false;
	}
}

export default new AuthService();
